import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Card,
  CardContent,
} from "@mui/material";
import {
  Edit as EditIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  ChangeCircle as ChangeCircleIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { Switch, FormControlLabel, Divider } from "@mui/material";
import api from "../../services/api";

const getTranslatedText = (translationObj, defaultLang = 'ja') => {
  if (!translationObj) return '';
  
  if (typeof translationObj !== 'object') return translationObj;
  
  return translationObj[defaultLang] || translationObj.ja || translationObj.en || translationObj.zh || '';
};

const InventoryManagement = () => {
  const { storeId } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);

  // 棚卸しモード用の状態
  const [activeTab, setActiveTab] = useState(0);
  const [stocktakeMode, setStocktakeMode] = useState(false);
  const [physicalCounts, setPhysicalCounts] = useState({});
  const [stocktakeLoading, setStocktakeLoading] = useState(false);
  const [stocktakeNote, setStocktakeNote] = useState("");

  // L-2 FIX: 在庫設定ダイアログ用の状態
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inventorySettings, setInventorySettings] = useState({
    defaultLowStockThreshold: 10,
    defaultReorderPoint: 5,
    alertsEnabled: true,
    alertEmails: [],
    slackWebhookUrl: "",
    autoHideOutOfStock: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  useEffect(() => {
    fetchData();
    fetchInventorySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // L-2 FIX: 在庫設定を取得
  const fetchInventorySettings = async () => {
    try {
      const response = await api.get(`/api/stores/${storeId}/inventory-settings`);
      if (response.data.success && response.data.inventorySettings) {
        setInventorySettings(prev => ({
          ...prev,
          ...response.data.inventorySettings,
        }));
      }
    } catch (error) {
      console.error("在庫設定の取得に失敗:", error);
    }
  };

  // L-2 FIX: 在庫設定を保存
  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await api.put(`/api/stores/${storeId}/inventory-settings`, inventorySettings);
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: "在庫設定を保存しました",
          severity: "success",
        });
        setSettingsOpen(false);
      }
    } catch (error) {
      console.error("在庫設定の保存に失敗:", error);
      setSnackbar({
        open: true,
        message: "在庫設定の保存に失敗しました",
        severity: "error",
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  // L-2 FIX: メールアドレス追加
  const handleAddEmail = () => {
    if (emailInput && emailInput.includes("@")) {
      setInventorySettings(prev => ({
        ...prev,
        alertEmails: [...(prev.alertEmails || []), emailInput],
      }));
      setEmailInput("");
    }
  };

  // L-2 FIX: メールアドレス削除
  const handleRemoveEmail = (email) => {
    setInventorySettings(prev => ({
      ...prev,
      alertEmails: (prev.alertEmails || []).filter(e => e !== email),
    }));
  };

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        api.get(`/api/menu/${storeId}/products`),
        api.get(`/api/menu/${storeId}/categories`).catch(() => ({ data: [] })),
      ]);
      const productList = Array.isArray(productsRes.data) ? productsRes.data : [];
      const categoryList = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
      setProducts(productList);
      setCategories(categoryList);
    } catch (error) {
      console.error("データの取得に失敗しました:", error);
      setProducts([]);
    }
  };

  const getCategoryName = (product) => {
    const categoryId = product.category_id || product.categoryId;
    if (!categoryId) return "-";
    const category = categories.find(c => (c.id || c._id) === categoryId);
    return category ? getTranslatedText(category.name) : "-";
  };

  const getStockEnabled = (product) => {
    return product.stock_enabled || product.manageStock || false;
  };

  const getStockQuantity = (product) => {
    return product.stock_current ?? product.stockQuantity ?? 0;
  };

  const getProductId = (product) => {
    return product.id || product._id;
  };

  const handleOpenDialog = (product) => {
    setSelectedProduct(product);
    setQuantity(getStockQuantity(product).toString());
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null);
    setQuantity("");
  };

  const handleUpdateStock = async () => {
    try {
      setLoading(true);
      const productId = getProductId(selectedProduct);

      // 在庫数を更新
      await api.patch(
        `/api/menu/${storeId}/products/${productId}/stock`,
        {
          quantity: parseInt(quantity),
        }
      );

      // 販売状態を更新
      await api.patch(
        `/api/menu/${storeId}/products/${productId}/availability`,
        {
          available: parseInt(quantity) > 0,
        }
      );

      // データを再取得して最新の状態を反映
      await fetchData();

      handleCloseDialog();
      setSnackbar({
        open: true,
        message: "在庫数を更新しました",
        severity: "success",
      });
    } catch (error) {
      console.error("在庫数の更新に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "在庫数の更新に失敗しました",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const isLowStock = (product) => {
    const threshold = product.low_stock_threshold ?? product.lowStockThreshold ?? 5;
    return (
      getStockEnabled(product) && getStockQuantity(product) <= threshold
    );
  };

  // ===== 棚卸し機能 =====

  // 棚卸しモードを開始
  const handleStartStocktake = () => {
    // 在庫管理が有効な商品のみを対象に、現在のシステム在庫で初期化
    const initialCounts = {};
    products
      .filter((p) => getStockEnabled(p))
      .forEach((p) => {
        const productId = getProductId(p);
        initialCounts[productId] = getStockQuantity(p);
      });
    setPhysicalCounts(initialCounts);
    setStocktakeMode(true);
    setStocktakeNote("");
  };

  // 棚卸しをキャンセル
  const handleCancelStocktake = () => {
    setStocktakeMode(false);
    setPhysicalCounts({});
    setStocktakeNote("");
  };

  // 物理在庫カウントを更新
  const handlePhysicalCountChange = (productId, value) => {
    const numValue = parseInt(value) || 0;
    setPhysicalCounts((prev) => ({
      ...prev,
      [productId]: Math.max(0, numValue),
    }));
  };

  // 棚卸しを実行
  const handleExecuteStocktake = async () => {
    setStocktakeLoading(true);
    try {
      // 変更があった商品のみを送信
      const adjustments = products
        .filter((p) => getStockEnabled(p))
        .filter((p) => {
          const productId = getProductId(p);
          const systemStock = getStockQuantity(p);
          const physicalCount = physicalCounts[productId];
          return physicalCount !== undefined && physicalCount !== systemStock;
        })
        .map((p) => ({
          productId: getProductId(p),
          physicalCount: physicalCounts[getProductId(p)],
        }));

      if (adjustments.length === 0) {
        setSnackbar({
          open: true,
          message: "変更のある商品がありません",
          severity: "info",
        });
        return;
      }

      const response = await api.post("/api/inventory/stocktake", {
        storeId,
        adjustments,
        note: stocktakeNote || "棚卸しによる調整",
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `棚卸し完了: ${response.data.adjustedCount}件の在庫を調整しました`,
          severity: "success",
        });
        // データを再取得
        await fetchData();
        handleCancelStocktake();
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || "棚卸しに失敗しました",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("棚卸しエラー:", error);
      setSnackbar({
        open: true,
        message: "棚卸しの実行に失敗しました",
        severity: "error",
      });
    } finally {
      setStocktakeLoading(false);
    }
  };

  // 変更がある商品数を計算
  const getChangedItemCount = () => {
    return products
      .filter((p) => getStockEnabled(p))
      .filter((p) => {
        const productId = getProductId(p);
        const systemStock = getStockQuantity(p);
        const physicalCount = physicalCounts[productId];
        return physicalCount !== undefined && physicalCount !== systemStock;
      }).length;
  };

  // 差異の合計を計算
  const getTotalDifference = () => {
    let total = 0;
    products
      .filter((p) => getStockEnabled(p))
      .forEach((p) => {
        const productId = getProductId(p);
        const systemStock = getStockQuantity(p);
        const physicalCount = physicalCounts[productId];
        if (physicalCount !== undefined) {
          total += physicalCount - systemStock;
        }
      });
    return total;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5">在庫管理</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* L-2 FIX: 在庫設定ボタン */}
          {!stocktakeMode && (
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setSettingsOpen(true)}
            >
              在庫設定
            </Button>
          )}
          {!stocktakeMode && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<InventoryIcon />}
              onClick={handleStartStocktake}
            >
              棚卸しを開始
            </Button>
          )}
        </Box>
      </Box>

      {/* 棚卸しモードのヘッダー */}
      {stocktakeMode && (
        <Card sx={{ mb: 2, bgcolor: "primary.light" }}>
          <CardContent>
            {stocktakeLoading && <LinearProgress sx={{ mb: 2 }} />}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ color: "primary.contrastText" }}>
                  棚卸しモード
                </Typography>
                <Typography variant="body2" sx={{ color: "primary.contrastText", opacity: 0.8 }}>
                  各商品の実際の在庫数を入力してください
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  icon={<ChangeCircleIcon />}
                  label={`変更: ${getChangedItemCount()}件`}
                  color={getChangedItemCount() > 0 ? "warning" : "default"}
                />
                <Chip
                  label={`差異合計: ${getTotalDifference() >= 0 ? "+" : ""}${getTotalDifference()}`}
                  color={getTotalDifference() !== 0 ? "error" : "success"}
                />
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="メモ（任意）"
                value={stocktakeNote}
                onChange={(e) => setStocktakeNote(e.target.value)}
                size="small"
                fullWidth
                placeholder="例: 月末定期棚卸し"
                sx={{ bgcolor: "white", borderRadius: 1 }}
              />
            </Box>
            <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleCancelStocktake}
                disabled={stocktakeLoading}
                sx={{ color: "white", borderColor: "white" }}
              >
                キャンセル
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleExecuteStocktake}
                disabled={stocktakeLoading || getChangedItemCount() === 0}
              >
                棚卸しを確定
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="在庫一覧" />
        <Tab label="在庫不足" />
      </Tabs>

      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: stocktakeMode ? 700 : 600 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>商品名</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>カテゴリー</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>システム在庫</TableCell>
              {stocktakeMode && <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>実在庫（入力）</TableCell>}
              {stocktakeMode && <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>差異</TableCell>}
              {!stocktakeMode && <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>在庫警告閾値</TableCell>}
              <TableCell sx={{ whiteSpace: "nowrap" }}>ステータス</TableCell>
              {!stocktakeMode && <TableCell sx={{ whiteSpace: "nowrap" }}>操作</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {products
              .filter((product) => {
                // タブによるフィルタリング
                if (activeTab === 1) {
                  return getStockEnabled(product) && isLowStock(product);
                }
                return true;
              })
              .map((product) => {
                const productId = getProductId(product);
                const systemStock = getStockQuantity(product);
                const physicalCount = physicalCounts[productId];
                const difference = stocktakeMode && physicalCount !== undefined
                  ? physicalCount - systemStock
                  : 0;

                return (
                  <TableRow
                    key={productId}
                    sx={{
                      backgroundColor: stocktakeMode && difference !== 0
                        ? difference > 0 ? "success.light" : "error.light"
                        : isLowStock(product)
                          ? "error.light"
                          : "inherit",
                    }}
                  >
                    <TableCell>{getTranslatedText(product.name)}</TableCell>
                    <TableCell>{getCategoryName(product)}</TableCell>
                    <TableCell align="right">
                      {getStockEnabled(product)
                        ? `${systemStock}個`
                        : "在庫管理なし"}
                    </TableCell>
                    {stocktakeMode && (
                      <TableCell align="right">
                        {getStockEnabled(product) ? (
                          <TextField
                            type="number"
                            value={physicalCount ?? ""}
                            onChange={(e) => handlePhysicalCountChange(productId, e.target.value)}
                            size="small"
                            sx={{ width: 100 }}
                            InputProps={{
                              inputProps: { min: 0 },
                              endAdornment: <Typography variant="caption">個</Typography>,
                            }}
                          />
                        ) : "-"}
                      </TableCell>
                    )}
                    {stocktakeMode && (
                      <TableCell align="right">
                        {getStockEnabled(product) && physicalCount !== undefined ? (
                          <Chip
                            size="small"
                            label={`${difference >= 0 ? "+" : ""}${difference}`}
                            color={difference === 0 ? "default" : difference > 0 ? "success" : "error"}
                          />
                        ) : "-"}
                      </TableCell>
                    )}
                    {!stocktakeMode && (
                      <TableCell align="right">
                        {getStockEnabled(product) ? `${product.low_stock_threshold ?? product.lowStockThreshold ?? 5}個` : "-"}
                      </TableCell>
                    )}
                    <TableCell>
                      {getStockEnabled(product) && isLowStock(product) && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <WarningIcon color="error" />
                          <Typography color="error">在庫不足</Typography>
                        </Box>
                      )}
                    </TableCell>
                    {!stocktakeMode && (
                      <TableCell>
                        {getStockEnabled(product) && (
                          <IconButton onClick={() => handleOpenDialog(product)}>
                            <EditIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>在庫数の更新</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography gutterBottom>
              商品名: {getTranslatedText(selectedProduct?.name)}
            </Typography>
            <TextField
              label="在庫数"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              fullWidth
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            onClick={handleUpdateStock}
            variant="contained"
            disabled={!quantity || parseInt(quantity) < 0}
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* L-2 FIX: 在庫設定ダイアログ */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>在庫設定</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              アラート閾値
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="在庫警告閾値"
                type="number"
                value={inventorySettings.defaultLowStockThreshold}
                onChange={(e) => setInventorySettings(prev => ({
                  ...prev,
                  defaultLowStockThreshold: parseInt(e.target.value) || 0,
                }))}
                fullWidth
                InputProps={{ inputProps: { min: 0 }, endAdornment: <Typography variant="caption">個</Typography> }}
                helperText="この数量以下で「在庫不足」と表示"
              />
              <TextField
                label="発注点"
                type="number"
                value={inventorySettings.defaultReorderPoint}
                onChange={(e) => setInventorySettings(prev => ({
                  ...prev,
                  defaultReorderPoint: parseInt(e.target.value) || 0,
                }))}
                fullWidth
                InputProps={{ inputProps: { min: 0 }, endAdornment: <Typography variant="caption">個</Typography> }}
                helperText="この数量以下で発注を推奨"
              />
            </Box>

            <Divider />

            <Typography variant="subtitle2" color="text.secondary">
              通知設定
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={inventorySettings.alertsEnabled}
                  onChange={(e) => setInventorySettings(prev => ({
                    ...prev,
                    alertsEnabled: e.target.checked,
                  }))}
                />
              }
              label="在庫アラート通知を有効化"
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>通知先メールアドレス</Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="email@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddEmail()}
                  fullWidth
                />
                <Button variant="outlined" onClick={handleAddEmail}>追加</Button>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {(inventorySettings.alertEmails || []).map((email, i) => (
                  <Chip
                    key={i}
                    label={email}
                    size="small"
                    onDelete={() => handleRemoveEmail(email)}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              label="Slack Webhook URL"
              value={inventorySettings.slackWebhookUrl || ""}
              onChange={(e) => setInventorySettings(prev => ({
                ...prev,
                slackWebhookUrl: e.target.value,
              }))}
              fullWidth
              placeholder="https://hooks.slack.com/services/..."
              helperText="Slackチャンネルへ通知を送信"
            />

            <Divider />

            <Typography variant="subtitle2" color="text.secondary">
              自動処理
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={inventorySettings.autoHideOutOfStock}
                  onChange={(e) => setInventorySettings(prev => ({
                    ...prev,
                    autoHideOutOfStock: e.target.checked,
                  }))}
                />
              }
              label="在庫切れ商品を自動で非表示にする"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            disabled={settingsLoading}
          >
            {settingsLoading ? "保存中..." : "保存"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryManagement;
